<template>
  <div class="god_page_container">
    <div class="page-title-row">
      <h2>Gestión de Monedero</h2>
      <button class="main_button-green" @click="addMovement()">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
        Añadir movimiento
      </button>
    </div>

    <div class="filters-container">
      <input type="text" placeholder="Filtrar por nombre o email de usuario..." v-model="filterText" class="input-text"/>
    </div>

    <div class="table-container">
      <table>
        <thead>
          <tr>
            <th class="big">Usuario</th>
            <th class="big">Descripción</th>
            <th class="small">Cantidad pendiente</th>
            <th class="small">Fecha caducidad</th>
            <th class="small">Fecha creación</th>
            <th class="small">Estado</th>
            <th class="small">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="(item, index) in movements
              .filter(movement => !filterText ||
                (movement.user_name && movement.user_name.toLowerCase().includes(filterText.toLowerCase())))"
            :key="item.id"
            :class="index % 2 === 0 ? 'bg-gray-100' : 'bg-white'"
          >
            <td style="text-align: left; font-weight: 600;">{{ item.user_name }}</td>
            <td style="text-align: left;">{{ item.title }}</td>
            <td><strong>{{ formatAmount(item.pending_amount) }}€</strong> <span class="text-subtle">/ {{ formatAmount(item.amount) }}€</span></td>
            <td>{{ item.expiration_date === null ? 'No caduca' : item.expiration_date }}</td>
            <td>{{ item.movement_date }}</td>
            <td>
              <span class="badge" :class="item.status === 'activo' || item.status === 'completado' ? 'badge-success' : 'badge-neutral'">
                {{ item.status }}
              </span>
            </td>
            <td>
              <div class="actions-cell">
                <img class="action_icon" title="Editar" src="@/assets/images/ic_action_edit.png" @click="onEditClicked(item)"/>
                <img class="action_icon" title="Eliminar" src="@/assets/images/ic_wallet_delete.png" @click="onDeleteClicked(item)"/>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <WalletMovementPopup
      :show-movement-popup="showMovementPopup"
      :movement="selectedMovement"
      @movementSaved="onMovementSaved"
      @movementPopupCancelled="movementPopupCancelled"
      @errorHappened="errorHappened"
      />

    <TwoButtonPopup 
      :title="deleteMovementTitle"
      :message="deleteMovementMessage"
      acceptButton="Borrar movimiento"
      @acceptCallback="() => deleteMovementConfirmed(movementToDelete)"
      cancelButton="Cancelar"
      @cancelCallback="deleteMovementCancelled"
      />

    <ErrorPopup :errorMessage="error" />

  </div>

</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick, watch } from "vue";
import { useRoute, useRouter } from 'vue-router';
import { useLoadingStore } from "@/stores/loadingStore";
import { refreshToken } from "@/services/authService";
import { formatAmount } from '@/utils/helpers';

import ErrorPopup from "@/components/common/ErrorPopup.vue";
import TwoButtonPopup from "@/components/common/TwoButtonPopup.vue";
import WalletMovementPopup from "@/components/wallet/WalletMovementPopup.vue";

const error = ref(null);
const movements = ref([]);
const loadingStore = useLoadingStore();
const selectedPurchaseData = ref(null);
const router = useRouter();
const route = useRoute();
const filterText = ref(route.query.filterText || "");

watch(filterText, () => {
  router.replace({
    query: {
      ...route.query,
      filterText: filterText.value
    }
  });
});

const showMovementPopup = ref(false);
const selectedMovement = ref(null);

const deleteMovementTitle = ref("");
const deleteMovementMessage = ref("");
const movementToDelete = ref(null);

const getWalletMovements = async () => {
  loadingStore.setLoading(true);
  error.value = null;

  try {
    let response = await fetchWalletMovements();

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        response = await fetchWalletMovements();
      } else {
        loadingStore.setLoading(false);
        localStorage.removeItem('user_id');
        localStorage.removeItem('token');
        router.push('/');
        return;
      }
    }

    if (!response.ok) {
      showError("Se ha producido un error obteniendo los movimientos.");
      return;
    }

    const data = await response.json();

    if (data.errors) {
      error.value = data.message;
      movements.value = [];
    } else {
      error.value = null;
      movements.value = data.movements;
    }
  } catch (exception) {
    error.value = exception.message;
  } finally {
    loadingStore.setLoading(false);
  }
};

async function fetchWalletMovements() {
  return await fetch(process.env.VUE_APP_API_URL+"api/god/wallet/get?user_id="+localStorage.getItem('user_id'), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "vgod": process.env.VUE_APP_API_VGOD,
        "cp-token": process.env.VUE_APP_API_CP_TOKEN,
        "Authorization": "Bearer " + localStorage.getItem('token')
      },
    });
}

getWalletMovements();

const handlePopState = () => {
  selectedPurchaseData.value = null;
};

onMounted(() => {
  window.addEventListener("popstate", handlePopState);
});

onUnmounted(() => {
  window.removeEventListener("popstate", handlePopState);
});

const onEditClicked = (item) => {
  selectedMovement.value = item;
  showMovementPopup.value = true;
};

const onMovementSaved = () => {
  showMovementPopup.value = false;
  getWalletMovements();
};

const movementPopupCancelled = () => {
  showMovementPopup.value = false;
}

const errorHappened = (message) => {
  showError(message);
}

const onDeleteClicked = (item) => {
  deleteMovementTitle.value = "Borrar movimiento";
  deleteMovementMessage.value = "¿Estás seguro de que deseas borrar este movimiento de "+item.user_name+"?";
  movementToDelete.value = item;
};

const showError = async (message) => {
  error.value = null;
  await nextTick(); // Espera a que Vue reaccione al cambio
  error.value = message;
};

const deleteMovementConfirmed = () => {
  if (movementToDelete.value) {
    deleteMovementTitle.value = null;
    deleteMovement();
  }
};

const deleteMovementCancelled = () => {
  deleteMovementTitle.value = null;
  movementToDelete.value = null;
};

const deleteMovement = async () => {
  loadingStore.setLoading(true);
  error.value = null;

  try {
    let response = await callAPIToDeleteMovement();

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        response = await callAPIToDeleteMovement();
      } else {
        loadingStore.setLoading(false);
        localStorage.removeItem('user_id');
        localStorage.removeItem('token');
        router.push('/');
        return;
      }
    }

    movementToDelete.value = null;

    if (!response.ok) {
      showError("Se ha producido un error eliminando el movimiento.");
      return;
    }

    const data = await response.json();

    if (data.errors) {
      error.value = data.message;
    } else {
      getWalletMovements();
    }
  } catch (exception) {
    error.value = exception.message;
  } finally {
    loadingStore.setLoading(false);
  }
};

async function callAPIToDeleteMovement() {
  return await fetch(process.env.VUE_APP_API_URL+"api/god/wallet/delete", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "vgod": process.env.VUE_APP_API_VGOD,
        "cp-token": process.env.VUE_APP_API_CP_TOKEN,
        "Authorization": "Bearer " + localStorage.getItem('token')
      },
      body: JSON.stringify({
        "user_id": localStorage.getItem('user_id'),
        "movement_id": movementToDelete.value.id,
      }),
    });
}

async function addMovement() {
  selectedMovement.value = null;
  showMovementPopup.value = true;
}

onMounted(() => {
  // filterText.value = route.query.user_email !== undefined ? route.query.user_email : ""; // Remove old logic that overwrote filterText with user_email, unless that was intended behavior. The user asked for state persistence.
  // The original code had: filterText.value = route.query.user_email ...
  // This likely meant "if I come from Users page clicking Wallet, filter by that user".
  // My new logic: `filterText = ref(route.query.filterText || "")`.
  // If I want to support BOTH "coming from Users page" AND "persisting my own filter":
  // The "coming from Users page" logic puts `user_email` in query.
  // So validation:
  if (route.query.user_email) {
     filterText.value = route.query.user_email;
  }
});

</script>

<style scoped>
.actions-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.text-subtle {
  font-size: 12px;
  color: var(--text-color-light);
}

.big {
  width: 24%;
}

.small {
  width: 12%;
}

.filters-container .input-text {
  max-width: 400px;
}

@media (max-width: 768px) {
  .filters-container .input-text {
    max-width: 100%;
  }

  .actions-cell {
    flex-wrap: wrap;
    gap: 6px;
  }
}
</style>
